// @flow

import type { ApolloResponseType } from 'types/ApolloType';

import { mergeIntoDefault } from 'utils/objectUtils';
import { formatDateWithMonthYear } from 'utils/dateUtils';

import {
  nullCompetitionResult,
  nullResult,
  nullResultDetail,
  nullStructure,
  nullResultCalendar,
  type CompetitionResultType,
  type ResultDetailType,
  type ResultType,
  type StructureType,
  type ResultCalendarType,
  type ResultCalendarContainerType,
} from 'types/Result';

const convertResultFromGraph = (rawGraphResponse: ApolloResponseType): CompetitionResultType[] => {
  if (!rawGraphResponse || !rawGraphResponse.data) {
    return [nullCompetitionResult];
  }

  return rawGraphResponse.data.Rencontres.reduce((acc, current) => {
    const {
      Competition: { id: competitionId, nom: competitionNom, identifiant: competitionIdentifiant, Saison: saison },
      Journee: { id: journeeId, nom: journeeNom },
      RencontreResultatVisiteuse: rawResultVisiteur,
      RencontreResultatLocale: rawResultLocal,
      CompetitionEquipeLocale: rawStructureLocal,
      CompetitionEquipeVisiteuse: rawStructureVisiteur,
      Etat: { nom: etat },
      etatMixte,
      id,
      dateOfficielle,
      dateEffective,
    } = current;

    const competitionIndex = acc.findIndex((comp) => comp.id === competitionId);

    const resultLocal: ResultDetailType = mergeIntoDefault(nullResultDetail, { ...rawResultLocal });
    const resultVisiteur: ResultDetailType = mergeIntoDefault(nullResultDetail, { ...rawResultVisiteur });

    const localStructure: StructureType = mergeIntoDefault(nullStructure, rawStructureLocal.Structure
      ? { ...rawStructureLocal.Structure, isRegroupement: false, nom: rawStructureLocal.nom.replace(/\(.*\)/g, '') }
      : { ...rawStructureLocal.Regroupement, isRegroupement: true, nom: rawStructureLocal.nom.replace(/\(.*\)/g, '') });

    const visiteurStructure: StructureType = mergeIntoDefault(nullStructure, rawStructureVisiteur.Structure
      ? { ...rawStructureVisiteur.Structure, isRegroupement: false, nom: rawStructureVisiteur.nom.replace(/\(.*\)/g, '') }
      : { ...rawStructureVisiteur.Regroupement, isRegroupement: true, nom: rawStructureVisiteur.nom.replace(/\(.*\)/g, '') });

    const result: ResultType = mergeIntoDefault(nullResult, {
      id,
      resultVisiteur,
      resultLocal,
      localStructure,
      visiteurStructure,
      etat: (etatMixte === 'En direct') ? 'En direct' : etat,
      dateOfficielle: new Date(dateOfficielle),
      dateEffective: new Date(dateEffective),
      dateEffectiveStr: dateEffective,
      competitionId,
      competitionNom,
      competitionIdentifiant,
      journeeId,
      journeeNom,
    });


    const journee = {
      id: journeeId,
      nom: journeeNom,
      competitionId: competitionId,
      competitionNom: competitionNom,
      date: new Date(dateEffective),
      rencontres: [result],
    };

    if (competitionIndex !== -1) {
      const journeeIndex = acc[competitionIndex]
        .journees
        .findIndex((jour) => jour.id === journeeId);

      if (journeeIndex !== -1) {
        acc[competitionIndex].journees[journeeIndex].rencontres.push(result);
      } else {
        acc[competitionIndex].journees.push(journee);
      }
    } else {
      acc.push({
        id: competitionId,
        nom: competitionNom,
        saison: saison,
        identifiant: competitionIdentifiant,
        journees: [journee]
      });
    }

    return acc;
  }, []);
};

const convertStructureFromMiddleware = (rawEquipe: Object): StructureType => {
  const { nom, structureId, regroupementId } = rawEquipe || {};
  const rawStructure = structureId || regroupementId || {};

  return mergeIntoDefault(nullStructure, {
    ...rawStructure,
    id: rawStructure.id ? String(rawStructure.id) : undefined,
    isRegroupement: !structureId,
    nom: (nom || rawStructure.nom || '').replace(/\(.*\)/g, ''),
  });
};

// Réponse de /club-websites/result-calendar : le middleware ne renvoie que les rencontres à venir ou en direct.
const convertResultCalendarFromMiddleware = (rawRencontres: Object[]): ResultCalendarContainerType => {
  if (!rawRencontres || rawRencontres.length === 0) {
    return {};
  }

  return rawRencontres
    .filter(({ terrainId }) => !!terrainId)
    .sort((a, b) => a.dateEffective > b.dateEffective ? 1 : a.dateEffective < b.dateEffective ? -1 : 0)
    .reduce((acc, current) => {
      const {
        id,
        dateOfficielle,
        etatMixte,
        competitionId: rawCompetition,
        competitionJourId: rawJournee,
        competitionEquipeLocaleId: rawEquipeLocale,
        competitionEquipeVisiteuseId: rawEquipeVisiteuse,
        terrainId: { id: terrainId, nom: terrainNom, adresseId: Adresse },
      } = current;
      const dateEffective = current.dateEffective || dateOfficielle;

      const {
        id: competitionId,
        nom: competitionNom,
        classeAgeId: rawClasseAge,
        saisonId: saison,
        competitionFamilleId: rawFamille,
      } = rawCompetition || {};
      const { localite, codePostal, voie, position } = Adresse || {};
      const address = Adresse ? `${voie} ${codePostal} ${localite}`.replace(/null/, '').trim() : false;

      const result: ResultCalendarType = mergeIntoDefault(nullResultCalendar, {
        id: String(id),
        terrain: {
          id: String(terrainId),
          nom: terrainNom,
          localite: Adresse ? localite : false,
          address,
          coordinates: (position && position.coordinates) || null,
        },
        localStructure: convertStructureFromMiddleware(rawEquipeLocale),
        visiteurStructure: convertStructureFromMiddleware(rawEquipeVisiteuse),
        etat: etatMixte === 'en direct' ? 'En direct' : 'Planifiée',
        dateOfficielle: new Date(dateOfficielle),
        dateEffective: new Date(dateEffective),
        dateEffectiveStr: dateEffective,
        competitionId: String(competitionId),
        journeeId: rawJournee ? String(rawJournee.id) : undefined,
        competition: {
          id: String(competitionId),
          nom: competitionNom,
          famille: rawFamille && rawFamille.nom,
          age: rawClasseAge && rawClasseAge.code,
          sexe: rawClasseAge && rawClasseAge.sexe && rawClasseAge.sexe.code,
          saison: saison && { id: String(saison.id), nom: saison.nom },
        },
        journee: rawJournee ? { id: String(rawJournee.id), nom: rawJournee.nom } : undefined,
      });

      const dateFormated = formatDateWithMonthYear(new Date(dateEffective));

      if (Object.prototype.hasOwnProperty.call(acc, dateFormated)) {
        acc[dateFormated].push(result);
      } else {
        acc[dateFormated] = [result];
      }

      return acc;
    }, {});
};

export { convertResultFromGraph, convertResultCalendarFromMiddleware };
